import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Box, Button, Grid, MenuItem, Stack, TextField } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';

import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

import { InventoryReturnFormData } from '../types';

interface ReturnFormProps {
  formData: InventoryReturnFormData;
  companyDisabled: boolean;
  canEdit?: boolean;
  companyOptions: Array<{ company_id: string; company_name: string }>;
  statusOptions: Array<{ id: string; name: string }>;
  onChangeForm: (updates: Partial<InventoryReturnFormData>) => void;
  onSave: () => void;
}

export default function ReturnForm({
  formData,
  companyDisabled,
  canEdit = true,
  companyOptions,
  statusOptions,
  onChangeForm,
  onSave,
}: ReturnFormProps) {
  const readOnly = !canEdit;

  return (
    <Stack spacing={4} sx={{ pt: 1, pb: 3 }}>
      <Grid container rowSpacing={0} columnSpacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label='Mã hoàn trả' labelWidth={120}>
            <TextField size='small' value={formData.code} onChange={(event) => onChangeForm({ code: event.target.value })} fullWidth disabled={readOnly} />
          </LabeledField>
          <LabeledField label='Tên hoàn trả' labelWidth={120}>
            <TextField size='small' value={formData.name} onChange={(event) => onChangeForm({ name: event.target.value })} fullWidth disabled={readOnly} />
          </LabeledField>
          <LabeledField label='Đơn hàng' labelWidth={120}>
            <TextField size='small' value={formData.order_no} onChange={(event) => onChangeForm({ order_no: event.target.value })} fullWidth disabled={readOnly} />
          </LabeledField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label='Kho' labelWidth={120}>
            <TextField size='small' value={formData.store_name} onChange={(event) => onChangeForm({ store_name: event.target.value })} fullWidth disabled={readOnly} />
          </LabeledField>
          <LabeledField label='Trạng thái' labelWidth={120}>
            <TextField select size='small' value={formData.status} onChange={(event) => onChangeForm({ status: event.target.value })} fullWidth disabled={readOnly}>
              {statusOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
              ))}
            </TextField>
          </LabeledField>
          <LabeledField label='Số lượng' labelWidth={120}>
            <NumericFormat
              customInput={TextField}
              size='small'
              value={formData.qty}
              onValueChange={({ value }) => onChangeForm({ qty: value })}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator='.'
              decimalSeparator=','
              allowNegative={false}
              fullWidth
              disabled={readOnly}
            />
          </LabeledField>
        </Grid>
      </Grid>

      <LabeledField label='Công ty' labelWidth={120}>
        <TextField select size='small' value={formData.company_id} onChange={(event) => onChangeForm({ company_id: event.target.value })} disabled={companyDisabled || readOnly} fullWidth>
          {companyOptions.map((company) => (
            <MenuItem key={company.company_id} value={company.company_id}>{company.company_name}</MenuItem>
          ))}
        </TextField>
      </LabeledField>

      <LabeledField label='Ghi chú' labelWidth={120} multiline fieldAlign='start' labelPosition='top'>
        <Box
          sx={{
            ...sharedFormStyles.noteSectionWrapper,
            width: '100%',
            '& textarea': {
              width: '100% !important',
              display: 'block',
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
          }}
        >
          <TextareaAutosize
            minRows={2}
            value={formData.note}
            onChange={(event) => onChangeForm({ note: event.target.value })}
            placeholder='Nhập ghi chú cho phiếu hoàn trả...'
            disabled={readOnly}
            style={{
              ...sharedFormStyles.noteTextarea(true),
              width: '100%',
              display: 'block',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          />
        </Box>
      </LabeledField>

      <Box sx={sharedFormStyles.actionBar}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='flex-end' spacing={2}>
          <Button
            variant='contained'
            onClick={onSave}
            size='small'
            startIcon={<SaveIcon />}
            disabled={readOnly}
            sx={sharedFormStyles.primaryButton}
          >
            Lưu hoàn trả
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
